import { useDispatch } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { MessageType, useMigrateMessageTypeMutation } from 'api/MessageTypeAPI';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { SubscriptionPolicy } from 'modules/graphql';
import { FC, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useForm, useFormContext } from 'react-hook-form';
import { BUTTON_SIZES, SecondaryButton } from 'ui/button';
import { MODAL_SIZES, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { MessageTypeSchema } from './ManageMessageTypeModal/ManageMessageTypeModal';
import { ManageDoubleOptInType } from './ManageMessageTypeModal/ManageDoubleOptIn';

export const DOI_DISCLAIMER_USERS =
  'Previously subscribed users will not be impacted. Newly subscribing users will be required to confirm their subscription after inputting their phone number.';
export const DOI_DISCLAIMER_FORM =
  'Double opt-in requires additional input which you will prompted to complete on the next page.';

type ModalProps = {
  onClose: () => void;
  messageType: MessageType;
  refetchMessageTypes: () => void;
};

export const withFormProvider = (Component: FC<ModalProps>) => (props: ModalProps) => {
  const methods = useForm({
    defaultValues: {
      messageTypeName: props.messageType?.name || '',
      subscriptionPolicy: SubscriptionPolicy.DoubleOptIn,
      brandName: '',
      messagingInitiative: '',
      smsDisclaimerLink: '',
      sender: undefined,
    },
    mode: 'onSubmit',
    resolver: zodResolver(MessageTypeSchema),
    reValidateMode: 'onChange',
  });

  return (
    <FormProvider {...methods}>
      <Component {...props} />
    </FormProvider>
  );
};

export const UpdateToDoubleOptInModal = withFormProvider(
  ({ onClose, messageType, refetchMessageTypes }: ModalProps) => {
    const dispatch = useDispatch();
    const [isShowingFormForUpdate, setIsShowingFormForUpdate] = useState(false);
    const { handleSubmit } = useFormContext();

    const { mutate } = useMigrateMessageTypeMutation({
      onSuccess: () => {
        onClose();
        refetchMessageTypes();
        dispatch(
          addPositiveToast(
            <>
              Success! Message type <strong>{messageType.name}</strong> updated to{' '}
              <strong>double opt-in</strong> subscription type
            </>,
          ),
        );
      },
      onError: (error) => {
        onClose();
        dispatch(
          addNegativeToastWithErrorId(
            <>
              Error: unable to update message type <strong>{messageType.name}</strong> to double
              opt-in subscription type. Please try again
            </>,
            'migrateMessageTypeToDoubleOptIn',
            error,
          ),
        );
      },
    });

    const onSubmit: SubmitHandler<FieldValues> = ({
      brandName,
      messagingInitiative,
      smsDisclaimerLink,
      sender,
    }) => {
      mutate({
        messageTypeId: messageType.id,
        brandName,
        messagingInitiative: [messagingInitiative],
        disclaimerLink: smsDisclaimerLink,
        smsSenderId: sender,
      });
    };

    return (
      <Modal
        id="update-to-double-opt-in-modal"
        onClose={onClose}
        isModalOpen={true}
        size={isShowingFormForUpdate ? MODAL_SIZES.LARGE : MODAL_SIZES.SMALL}
      >
        {isShowingFormForUpdate ? (
          <ManageDoubleOptInType
            onSubmit={handleSubmit(onSubmit)}
            actionLoading={false}
            onClose={onClose}
            isEditMode={false}
            isUpdateToDoubleOptInMode
            prevAction={() => setIsShowingFormForUpdate(false)}
          />
        ) : (
          <>
            <ModalHeader>Update message type to double opt-in?</ModalHeader>
            <ModalBody>
              {DOI_DISCLAIMER_USERS}
              <br />
              <br />
              {DOI_DISCLAIMER_FORM}
            </ModalBody>
            <ModalFooter hasCancel>
              <SecondaryButton
                onClick={() => setIsShowingFormForUpdate(true)}
                size={BUTTON_SIZES.LARGE}
              >
                Next
              </SecondaryButton>
            </ModalFooter>
          </>
        )}
      </Modal>
    );
  },
);
